import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Package } from "lucide-react";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/#products", label: "Products" },
  { to: "/#about", label: "About" },
  { to: "/#services", label: "Services" },
  { to: "/#leadership", label: "Leadership" },
  { to: "/clients", label: "Our Suppliers" },
  { to: "/#contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  // Auto-scroll to hash target when navigating to /#section from another page (e.g., from /clients)
  useEffect(() => {
    if (location.pathname === "/" && location.hash) {
      const id = location.hash.replace("#", "");
      // Small delay to allow the home page to mount its sections
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [location.pathname, location.hash]);

  const handleHashClick = (e, to) => {
    // If clicking "Home" (just "/") while already on the home page, scroll to top smoothly
    if (to === "/" && location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    // If clicking a hash link while on the home page, smooth-scroll to that section
    if (to.includes("#") && location.pathname === "/") {
      e.preventDefault();
      const id = to.split("#")[1];
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-white/85 border-b border-slate-200/80 shadow-sm" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" onClick={(e) => handleHashClick(e, "/")} className="flex items-center group">
          <img
            src="/logo-transparent.png"
            alt="High Roller Consumer Goods Trading"
            className="h-12 md:h-14 w-auto group-hover:scale-[1.03] transition-transform"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={(e) => handleHashClick(e, l.to)}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive && !l.to.includes("#")
                    ? "bg-slate-100 text-slate-900"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/#contact"
            onClick={(e) => handleHashClick(e, "/#contact")}
            data-testid="header-cta-quote"
            className="bg-blue-600 text-white hover:bg-blue-700 rounded-full px-5 py-2.5 text-sm font-medium transition-colors shadow-sm"
          >
            Request a Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 rounded-md text-slate-700"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 bg-white">
          <nav className="px-6 py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={(e) => handleHashClick(e, l.to)}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/#contact"
              data-testid="mobile-cta-quote"
              className="mt-2 bg-blue-600 text-white rounded-full px-5 py-3 text-center font-medium"
            >
              Request a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
